import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { sendLoginReportOnce } from '../services/report';

/**
 * Resolves the ExtraMilePlay session for this play.
 *
 * The platform launches the game with `?otp=...` (optionally `&role=GUEST_USER`);
 * we exchange that for a sessionId/organizationId and keep them for the
 * lifetime of the tab. `?demo=1` skips the exchange for local development.
 *
 * The backend's auth middleware expects `Bearer ${sessionId}&${organizationId}`,
 * which is exactly the shape stored under sessionStorage['session'].
 */

const SESSION_KEY = 'session';
const USER_KEY = 'orbUser';

const EM_LOGO = process.env.REACT_APP_EM_LOGO || '';

const EMPTY = { status: 'loading', token: null, userId: null, name: null, email: null, error: null, companyLogoUrl: EM_LOGO, backButtonUrl: null, fromMobileApp: false };

const SessionContext = createContext(EMPTY);

const makeIdempotencyKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const isTrue = (v) => String(v ?? '').toLowerCase() === 'true';

function readStoredSession() {
  try {
    const token = sessionStorage.getItem(SESSION_KEY);
    if (!token || !token.includes('&')) return null;
    const user = JSON.parse(sessionStorage.getItem(USER_KEY) || '{}');
    return {
      token,
      userId: user.userId || null,
      name: user.name || null,
      email: user.email || null,
      companyLogoUrl: user.companyLogoUrl || EM_LOGO,
      backButtonUrl: user.backButtonUrl || null,
      fromMobileApp: !!user.fromMobileApp,
      otp: user.otp || null,
    };
  } catch {
    return null;
  }
}

function persistSession(token, user) {
  try {
    sessionStorage.setItem(SESSION_KEY, token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* sessionStorage unavailable (private mode) — session stays in memory only */
  }
}

const clean = (v) => (v == null || /^(null|undefined)$/i.test(String(v).trim()) ? '' : String(v).trim());

/**
 * Who is playing, for the orb_sessions row: an email login stores the email
 * in `email` and the player's name in `name`; an employee-ID login (no email)
 * keeps the employee ID in `name`.
 */
function identityFromLogin(d) {
  const email = clean(d.email);
  const employeeId = clean(d.employeeId);
  const fullName = [clean(d.firstName), clean(d.lastName)].filter(Boolean).join(' ') || clean(d.name);
  return {
    name: (email ? fullName : employeeId || fullName) || null,
    email: email || null,
  };
}

/** Same rules as the other ExtraMilePlay games: org logo from S3, EM logo when blank. */
function buildLogoUrl(companyLogo) {
  const s = String(companyLogo ?? '').trim();
  if (!s || /^(null|undefined)$/i.test(s) || /\/(null|undefined)$/i.test(s)) return EM_LOGO;
  if (/^https?:\/\//i.test(s)) return s;
  const source = process.env.REACT_APP_S3_SOURCE || '';
  const base = source.endsWith('/') ? source : `${source}/`;
  return `${base}${s.replace(/^\/+/, '')}`;
}

async function fetchCompanyLogo(organizationId) {
  try {
    const { data } = await axios.get(`${process.env.REACT_APP_ORG_API}/${organizationId}`);
    if (data?.message !== 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') return EM_LOGO;
    return buildLogoUrl(data.organization?.companyLogo);
  } catch {
    return EM_LOGO;
  }
}

function makeDemoSession() {
  const rand = Math.floor(Math.random() * 1_000_000);
  return { sessionId: `demo${rand}`, organizationId: `demo${rand}`, userId: `demo${rand}` };
}

export function SessionProvider({ children }) {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const otp = params.get('otp');
    const role = params.get('role');
    const demo = params.get('demo');
    const fromMobileAppInUrl = isTrue(params.get('fromMobileApp'));

    // OTPs are single-use, so a refresh that still carries the launch OTP
    // must reuse the session it already produced instead of exchanging again.
    const stored = readStoredSession();
    if (stored && (!otp || stored.otp === otp)) {
      const { otp: _usedOtp, ...session } = stored;
      setState({ ...session, fromMobileApp: session.fromMobileApp || fromMobileAppInUrl, status: 'ready', error: null });
      return;
    }

    if (demo || (!otp && process.env.NODE_ENV === 'development')) {
      const { sessionId, organizationId, userId } = makeDemoSession();
      const token = `${sessionId}&${organizationId}`;
      const user = { userId, name: userId, email: null, companyLogoUrl: EM_LOGO, backButtonUrl: null, fromMobileApp: fromMobileAppInUrl };
      persistSession(token, user);
      setState({ ...user, status: 'ready', token, error: null });
      return;
    }

    if (!otp) {
      setState({ ...EMPTY, status: 'error', error: 'This game must be opened from ExtraMilePlay.' });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const isGuest = role === 'GUEST_USER';
        const apiUrl = isGuest
          ? process.env.REACT_APP_FIREBASE_GUEST_USER_API
          : process.env.REACT_APP_FIREBASE_LOGIN_API;

        const { data } = await axios.post(
          apiUrl,
          isGuest ? { otp, role: 'GUEST_USER' } : { otp },
          {
            headers: {
              'client-secret': process.env.REACT_APP_CLIENT_SECRET,
              'Content-Type': 'application/json',
              'Idempotency-Key': makeIdempotencyKey(),
            },
          }
        );

        if (cancelled) return;
        if (data?.message !== 'LOGIN SUCCESS') throw new Error('Login failed');

        const loginData = data.data || {};
        const { sessionId, organizationId, userId, id, gameId, token: authToken } = loginData;
        if (!sessionId || !organizationId) throw new Error('Session could not be established');

        const companyLogoUrl = await fetchCompanyLogo(organizationId);
        if (cancelled) return;

        const token = `${sessionId}&${organizationId}`;
        const user = {
          userId: userId || id || null,
          ...identityFromLogin(loginData),
          companyLogoUrl,
          backButtonUrl: gameId ? `${process.env.REACT_APP_BASE_URL}/game-detail/${gameId}` : null,
          fromMobileApp: fromMobileAppInUrl,
        };
        persistSession(token, { ...user, otp });
        setState({ ...user, status: 'ready', token, error: null });

        // Score 0 / time 00:00 report, inserted once per player and never updated.
        sendLoginReportOnce({
          sessionToken: token,
          isGuest,
          authToken,
          sessionId,
          organizationId,
          userId: user.userId,
          gameId,
          name: user.name,
          email: user.email,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          ...EMPTY,
          status: 'error',
          fromMobileApp: fromMobileAppInUrl,
          error: err?.response?.data?.message || err.message || 'Session expired',
        });
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => state, [state]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}
